import { Navigate } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useAuth } from '@/contexts/AuthContext';

/**
 * A guard that WAITS rather than guessing.
 *
 * Two separate ticks can look like 'not allowed' when they are only 'not
 * known yet': the stored session before Firebase has read it, and the role
 * listeners a tick after that. Redirecting during either throws the user out
 * of the very page they have the right to see, on every refresh — /tracks
 * bounced to /home in production for exactly this reason, which made the
 * page impossible to link to or reload.
 *
 * Being an admin still never stands in for being a معلم: those roles are
 * separate everywhere else in this system.
 */
export function RequireAuth({ role, children }: { role?: 'muqri' | 'admin'; children: ReactNode }) {
  const { identity, isMuqri, isAdmin, loading, restoring } = useAuth();
  if (!identity) {
    if (restoring) return <div data-testid="auth-restoring" />;
    return <Navigate to="/" replace />;
  }
  if (role && loading) return <div data-testid="role-loading" />;
  if (role === 'admin' && !isAdmin) return <Navigate to="/unauthorized" replace />;
  if (role === 'muqri' && !isMuqri) return <Navigate to="/unauthorized" replace />;
  return <>{children}</>;
}
